import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import qs from 'qs'
import UtmUtils from '../utils/utmUtils'
import { atcb_action } from 'add-to-calendar-button-react'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { useStore } from './useStore'
import { useEventById } from './use-event-by-id'
import { setShowAskNameDialog } from '../reducers/uiSlice'
import { useUtm } from '@gojiraf/useutm'

const formatGoogleDateTime = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${year}${month}${day}T${hours}${minutes}00`
}

const buildGoogleCalendarUrl = ({ name, description, location, startDate, endDate, timeZone }) => {
  const searchParams = new URLSearchParams({
    action: 'TEMPLATE',
    text: name,
    details: description,
    location,
    dates: `${formatGoogleDateTime(startDate)}/${formatGoogleDateTime(endDate)}`,
  })

  if (timeZone) {
    searchParams.set('ctz', timeZone)
  }

  return `https://calendar.google.com/calendar/render?${searchParams.toString()}`
}

export const useAddToCalendar = () => {
  const dispatch = useDispatch()
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium, utm_campaign } = UtmUtils.getUtmObject(queryParams)
  const { isAppView } = useUtm(utm_medium)
  const { postHogTrackEvent } = usePostHogAnalytics()
  const { data: event } = useEventById()
  const { data: store } = useStore()

  const url = useMemo(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('utm_source', 'calendar')
    url.searchParams.set('utm_medium', utm_medium ?? 'calendar')
    url.searchParams.set('utm_campaign', utm_campaign ?? 'socios')
    return url.toString()
  }, [])

  const startDate = useMemo(
    () => new Date(event?.startAt ?? event?.joinAt),
    [event?.startAt, event?.joinAt],
  )
  const endDate = useMemo(() => new Date(startDate.getTime() + 60 * 60 * 1000), [startDate])
  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const addToCalendar = () => {
    if (!store || !event) return

    dispatch(setShowAskNameDialog({ open: false }))

    postHogTrackEvent('add-to-calendar-button', { storeId: store.id, eventId: event.eventId })

    const startYear = startDate.getFullYear()
    const startMonth = String(startDate.getMonth() + 1).padStart(2, '0')
    const startDay = String(startDate.getDate()).padStart(2, '0')
    const startHours = String(startDate.getHours()).padStart(2, '0')
    const startMinutes = String(startDate.getMinutes()).padStart(2, '0')
    const endYear = endDate.getFullYear()
    const endMonth = String(endDate.getMonth() + 1).padStart(2, '0')
    const endDay = String(endDate.getDate()).padStart(2, '0')
    const endHours = String(endDate.getHours()).padStart(2, '0')
    const endMinutes = String(endDate.getMinutes()).padStart(2, '0')

    if (isAppView) {
      const googleCalendarUrl = buildGoogleCalendarUrl({
        name: `${store.name} - ${event.name}`,
        description: event.description ?? '',
        location: url,
        startDate,
        endDate,
        timeZone,
      })

      window.location.assign(googleCalendarUrl)
      return
    }

    atcb_action({
      name: `${store.name} - ${event.name}`,
      description: event.description ?? '',
      options: ['Google', 'Outlook.com', 'Apple'],
      startDate: `${startYear}-${startMonth}-${startDay}`,
      endDate: `${endYear}-${endMonth}-${endDay}`,
      startTime: `${startHours}:${startMinutes}`,
      endTime: `${endHours}:${endMinutes}`,
      timeZone,
      location: url,
      trigger: 'click',
      listStyle: 'modal',
    })
  }

  return { addToCalendar }
}
